import React, { Component } from 'react';
import _LanguageConfigService from 'modules/LanguageConfigService';
import _notification from 'modules/notification';
import { Table, Tag } from 'antd';
import LoadingSpinner from 'components/enterprise/LoadingSpinner';
import PageHeader from 'components/enterprise/PageHeader';
import DropdownList from 'components/controls/DropdownList';
import { routesAdmin } from 'routes/Routes';
import ButtonComponent from 'components/controls/Button';
import { GENERAL_ERROR_MESSAGE } from 'constants/constants';

// Phase C.2 (Enterprise Language Configuration Foundation): Admin-controlled Portal Language Mode
// (English Only / English + Regional Language) foundation screen, plus a reference/management list of
// known languages. This is NOT the existing candidate-facing language selector (LanguageContext.js /
// AccessibilityToolbar.jsx, backed by the separate EnterpriseConfiguration Category='Language' rows) —
// that mechanism is deliberately left untouched by this phase. This new configuration is not yet consumed
// by any Candidate-facing rendering (Phase C.3's scope).
const MODE_OPTIONS = [
    { Id: 'EnglishOnly', Name: 'English Only' },
    { Id: 'EnglishPlusRegional', Name: 'English + Regional Language' }
];

class LanguageConfiguration_new extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            mode: 'EnglishOnly',
            regionalLanguageCode: '',
            selectableLanguages: [],
            languageMasterList: [],
            formError: ''
        };
    }

    componentDidMount() {
        this.loadAll();
    }

    loadAll = async () => {
        this.setState({ loading: true });
        try {
            const [configRes, selectableRes, masterRes] = await Promise.all([
                _LanguageConfigService.getPortalLanguageConfig(),
                _LanguageConfigService.getSelectableRegionalLanguages(),
                _LanguageConfigService.getLanguageMasterList()
            ]);
            this.setState({
                mode: configRes.data.Mode || 'EnglishOnly',
                regionalLanguageCode: configRes.data.RegionalLanguageCode || '',
                selectableLanguages: selectableRes.data || [],
                languageMasterList: masterRes.data || [],
                loading: false
            });
        } catch (e) {
            this.setState({ loading: false });
            console.log('Error: ', e);
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    onModeChange = (event) => {
        const mode = event.target.value;
        this.setState({
            mode,
            // Mirrors the server's own normalize-on-EnglishOnly behavior, so the form never shows a
            // stray Regional Language selection sitting alongside English Only.
            regionalLanguageCode: mode === 'EnglishOnly' ? '' : this.state.regionalLanguageCode,
            formError: ''
        });
    }

    onRegionalChange = (event) => {
        this.setState({ regionalLanguageCode: event.target.value, formError: '' });
    }

    validate = () => {
        if (this.state.mode === 'EnglishPlusRegional' && !this.state.regionalLanguageCode) {
            return 'Please select a Regional Language.';
        }
        return '';
    }

    handleSave = async () => {
        const error = this.validate();
        if (error) {
            this.setState({ formError: error });
            return;
        }

        this.setState({ loading: true });
        try {
            const response = await _LanguageConfigService.updatePortalLanguageConfig({
                Mode: this.state.mode,
                RegionalLanguageCode: this.state.mode === 'EnglishOnly' ? null : this.state.regionalLanguageCode
            });
            if (response.status === 200) {
                _notification.showMessage('success', 'Portal Language Configuration saved.');
                this.loadAll();
            } else {
                this.setState({ loading: false });
                _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ loading: false });
            console.log('Error: ', e);
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    onToggleActive = async (record) => {
        try {
            const response = await _LanguageConfigService.updateLanguageActive(record.Id, !record.IsActive);
            if (response.status === 200) {
                _notification.showMessage('success', (record.IsActive ? 'Deactivated ' : 'Activated ') + record.LanguageName + '.');
                this.loadAll();
            } else {
                _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            console.log('Error: ', e);
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    render() {
        const { mode, regionalLanguageCode, selectableLanguages, languageMasterList, formError } = this.state;

        const TableColumns = [
            { title: 'Code', dataIndex: 'LanguageCode', key: 'LanguageCode', width: '12%' },
            { title: 'Language Name', dataIndex: 'LanguageName', key: 'LanguageName', width: '23%' },
            { title: 'Native Name', dataIndex: 'NativeName', key: 'NativeName', width: '23%' },
            { title: 'Order', dataIndex: 'DisplayOrder', key: 'DisplayOrder', width: '10%' },
            {
                title: 'Status', dataIndex: 'IsActive', key: 'IsActive', width: '18%',
                render: (value, record) => (
                    <>
                        <Tag color={value ? 'green' : undefined}>{value ? 'Active' : 'Inactive'}</Tag>
                        {/* Phase C.2.1 Objective A/§7: surfaces the active-language dependency instead of
                            letting the admin discover it only after a rejected Deactivate click. */}
                        {record.IsCurrentRegionalLanguage &&
                            <div className="mt-1"><small>Currently configured as Regional Language</small></div>}
                    </>
                )
            },
            {
                title: 'Action', dataIndex: '', key: 'Id', width: '14%',
                render: (record) => (
                    record.LanguageCode === 'en'
                        ? <span>Base language</span>
                        : <ButtonComponent type="button" value={record.IsActive ? 'Deactivate' : 'Activate'}
                            className={"button-global small autoHeight " + (record.IsActive ? 'button-danger' : 'button-primary')}
                            onClick={() => this.onToggleActive(record)}
                            disabled={record.IsCurrentRegionalLanguage} />
                )
            }
        ];

        if (this.state.loading && languageMasterList.length === 0)
            return <LoadingSpinner />

        return (
            <div className="container-fluid p-3">
                <PageHeader
                    title="Language Configuration"
                    breadcrumbItems={[{ label: 'Dashboard', path: routesAdmin.dashboard.path }, { label: 'Language Configuration' }]}
                />
                <div className="analysisWrap">
                    <div className="max1200 m-auto">
                        <div className="ent-panel">
                            <h4 className="heading4">Portal Language Mode</h4>
                            <div className="admin-inner-wrap p-3">
                                <div className="row">
                                    <div className="col-sm-6 mb-2">
                                        <DropdownList
                                            name="Mode"
                                            value={mode}
                                            onChange={this.onModeChange}
                                            placeholder="Select Portal Language Mode"
                                            heading="Portal Language Mode"
                                            isRequired={true}
                                            options={MODE_OPTIONS}
                                        />
                                    </div>
                                    <div className="col-sm-6 mb-2">
                                        <DropdownList
                                            name="RegionalLanguageCode"
                                            value={regionalLanguageCode}
                                            onChange={this.onRegionalChange}
                                            placeholder={mode === 'EnglishOnly' ? 'Not applicable' : 'Select Regional Language'}
                                            heading="Regional Language"
                                            isRequired={mode === 'EnglishPlusRegional'}
                                            isDisabled={mode === 'EnglishOnly'}
                                            options={selectableLanguages.map(l => ({
                                                Id: l.LanguageCode,
                                                Name: l.NativeName ? l.LanguageName + ' (' + l.NativeName + ')' : l.LanguageName
                                            }))}
                                        />
                                    </div>
                                </div>
                                {formError && <div className="color-red mb-2">{formError}</div>}
                                <div className="pt-2 buttonAlignCenter">
                                    <ButtonComponent type="button" value="Save" onClick={this.handleSave} className="button-global button-primary autoHeight small" disabled={this.state.loading} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="max1200 m-auto mt-4">
                        <h4 className="heading4">Language Master (reference)</h4>
                        <Table size='small'
                            bordered
                            rowKey='Id'
                            columns={TableColumns}
                            dataSource={languageMasterList}
                            pagination={false}
                            className="table-striped-rows admin-table"
                        />
                    </div>
                </div>
            </div>
        )
    };
}

export default LanguageConfiguration_new;
